/* eslint-disable max-statements */
import { Button, Center, Flex, Loader, ScrollArea, Stack, Tabs, Text } from '@mantine/core';
import { JsonEditor } from 'json-edit-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdminTemplatesEditFields, useCreateTemplate, useEditTemplate, useTemplate } from '@admin/hooks';
import { BackButton, LeavePageNotification, StickyContainer } from '@components';
import { useSettings } from '@hooks/useSettings';
import { TemplateVersions } from './TemplateVersions';
import { useEditTemplateFormInitializer } from './useEditTemplateFormInitializer';
import { useEditTemplateTabs } from './useEditTemplateTabs';

export const EditTemplate = () => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const { data: template, isLoading } = useTemplate(id, type);
  const [editedTemplate, setEditedTemplate] = useState(template);
  const [editDisabled, setEditDisabled] = useState(!!template?.brokerId);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('user-friendly');
  const { data: settings } = useSettings();
  const { data: editFields, isLoading: isEditFieldsLoading } = useAdminTemplatesEditFields({ type: template?.type });

  const editTemplateMutation = useEditTemplate();
  const createTemplateMutation = useCreateTemplate();

  const { form, buildInitialValues } = useEditTemplateFormInitializer({
    template: editedTemplate,
  });

  const handleChange = (newTemplateValue) => {
    form.setValues(buildInitialValues({ ...newTemplateValue }));
    form.setTouched({ _id: true }); // in order to trigger the leave page modal when JSON editing
    setEditedTemplate({ ...newTemplateValue });
  };

  const { tabs } = useEditTemplateTabs({
    form,
    editFields,
    editedTemplate,
    handleChange,
    editDisabled,
  });

  const handleEdit = async () => {
    setSaving(true);
    const savedTemplate = await (id ? editTemplateMutation : createTemplateMutation).mutateAsync({
      ...editedTemplate,
      updatedAt: null,
    });

    navigate(`/${settings.adminPath}/manage-templates/edit/${type}/${savedTemplate._id}`, { replace: true });

    setSaving(false);
  };

  useEffect(() => {
    if (template?._id && template.published === false) {
      navigate(`/${settings.adminPath}/manage-templates`, { replace: true });
      return;
    }
    setEditedTemplate(template);
    form.setValues(buildInitialValues(template));
    form.resetDirty();
    form.resetTouched();
    setEditDisabled(!!template?.brokerId);
  }, [template]);

  if (isLoading || isEditFieldsLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={!saving && form.isTouched() && form.isDirty()} />

      <Stack my={32} px={32}>
        <Flex align="center" gap={32}>
          <BackButton backPath={-1}></BackButton>
          <Text size="20px" fw={700}>
            {editedTemplate?.name}
          </Text>
        </Flex>
        <form>
          <Tabs h="100%" value={activeTab} onChange={(value) => setActiveTab(value)} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="user-friendly" data-pw="user-friendly-tab">
                User friendly
              </Tabs.Tab>
              <Tabs.Tab value="json-edit" data-pw="json-edit-tab">
                JSON edit
              </Tabs.Tab>
              {template?.versioningId && (
                <Tabs.Tab value="versions" data-pw="versions-tab">
                  Versions
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="user-friendly">
              <Tabs h="100%" defaultValue="general-info" orientation="vertical" keepMounted={false} py="lg">
                <Tabs.List>
                  {tabs.map((tab) => (
                    <Tabs.Tab key={tab.value} value={tab.value}>
                      {tab.label}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>

                {tabs.map((tab) => (
                  <Tabs.Panel key={tab.value} value={tab.value}>
                    <ScrollArea h="calc(100vh - 450px)" offsetScrollbars="y" px={24}>
                      {tab.component}
                    </ScrollArea>
                  </Tabs.Panel>
                ))}
              </Tabs>
            </Tabs.Panel>
            <Tabs.Panel value="json-edit">
              <ScrollArea h="calc(100vh - 400px)" offsetScrollbars="y" py={16}>
                <JsonEditor
                  data={form.values}
                  onUpdate={(data) => handleChange(data.newData)}
                  minWidth="100%"
                  rootName="template"
                  collapse={5}
                  collapseAnimationTime={300}
                  viewOnly={editDisabled}
                />
              </ScrollArea>
            </Tabs.Panel>
            {template?.versioningId && (
              <Tabs.Panel value="versions">
                <TemplateVersions versioningId={template.versioningId} templateType={type} editDisabled={editDisabled}></TemplateVersions>
              </Tabs.Panel>
            )}
          </Tabs>
        </form>

        {activeTab !== 'versions' && !editDisabled && (
          <StickyContainer noPl>
            <Button type="button" disabled={!form.isValid()} variant="filled" size="lg" onClick={handleEdit}>
              Save
            </Button>
          </StickyContainer>
        )}
      </Stack>
    </>
  );
};
